import React, { useState } from 'react';
import { AlertTriangle, BookmarkCheck, Loader2, RotateCcw, Undo2 } from 'lucide-react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : d?.message || 'Request failed';
};

/** Provenance, scope and revoke/restore for one governed remembered identity (DN + PSB). */
export function IdentityMemoryCard({ memory, onChanged, canEdit, testIdPrefix, children }) {
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!memory) return null;
  const active = memory.status === 'active';
  const flags = memory.needs_review || [];

  const call = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/matching/identities/${memory.memory_id}/${path}`, body);
      setRevoking(false);
      setReason('');
      onChanged?.(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded border px-2 py-1.5 text-[10px] space-y-1 ${active ? 'border-sky-300 bg-sky-50' : 'border-slate-300 bg-slate-50'}`} data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center gap-1 flex-wrap">
        <BookmarkCheck className={`h-3 w-3 ${active ? 'text-sky-700' : 'text-slate-500'}`} />
        <span className="font-semibold text-slate-800" data-testid={`${testIdPrefix}-status`}>
          {active ? 'Remembered identity' : 'Revoked identity (not suggested)'}: {memory.product?.product_name}
        </span>
        {memory.origin === 'legacy_promoted' && <span className="text-slate-500">· promoted from legacy mapping #{memory.legacy_alias_id}</span>}
      </div>
      <div className="text-slate-600" data-testid={`${testIdPrefix}-scope`}>Scope: {memory.scope_text}. Only the product link is reused, never prices, yields or overrides.</div>
      <div className="text-slate-500" data-testid={`${testIdPrefix}-provenance`}>
        Confirmed by {memory.confirmed_by || 'unknown'}{memory.confirmed_at ? ` on ${new Date(memory.confirmed_at).toLocaleString()}` : ''}
        {memory.source_filename ? ` · ${memory.source_filename}` : ''}{memory.source_site_name ? ` · ${memory.source_site_name}` : ''}
        {memory.psb_mc_id ? ` · PSB row ${memory.psb_mc_id}` : ''}
        {!active && memory.ended_by ? ` · revoked by ${memory.ended_by}${memory.end_reason ? ` (${memory.end_reason})` : ''}` : ''}
      </div>
      {active && flags.length > 0 && (
        <div className="flex items-start gap-1 text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-1" data-testid={`${testIdPrefix}-needs-review`}>
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
          <span>Needs review before reuse: {flags.join('; ')}</span>
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        {active && children}
        {canEdit && active && !revoking && (
          <Button size="sm" variant="ghost" className="h-5 text-[10px] gap-1 text-rose-700" onClick={() => setRevoking(true)} data-testid={`${testIdPrefix}-revoke`}>
            <Undo2 className="h-3 w-3" />Revoke
          </Button>
        )}
        {canEdit && !active && memory.status === 'revoked' && (
          <Button size="sm" variant="outline" className="h-5 text-[10px] gap-1" disabled={busy} onClick={() => call('restore')} data-testid={`${testIdPrefix}-restore`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}Restore
          </Button>
        )}
      </div>
      {revoking && (
        <div className="flex items-center gap-1 flex-wrap">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for revoking (required)" className="h-6 text-[10px] w-56" data-testid={`${testIdPrefix}-revoke-reason`} />
          <Button size="sm" className="h-6 text-[10px]" disabled={busy || reason.trim().length < 3} onClick={() => call('revoke', { reason })} data-testid={`${testIdPrefix}-revoke-confirm`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Revoke for future suggestions'}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setRevoking(false)} data-testid={`${testIdPrefix}-revoke-cancel`}>Cancel</Button>
        </div>
      )}
      {error && <div className="text-rose-700" data-testid={`${testIdPrefix}-error`}>{error}</div>}
    </div>
  );
}

/** Inline reasons for an identity action that was refused, plus an explicit correction path. */
export function IdentityIssue({ issue, onCorrect, onDismiss, busy, testIdPrefix }) {
  if (!issue) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900 space-y-1" data-testid={`${testIdPrefix}-issue`}>
      <div className="flex items-start gap-1"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" /><span>{issue.message}</span></div>
      {(issue.reasons || []).length > 0 && (
        <ul className="list-disc ml-5" data-testid={`${testIdPrefix}-issue-reasons`}>{issue.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      )}
      {issue.existing && (
        <div data-testid={`${testIdPrefix}-issue-existing`}>Currently remembered: {issue.existing.product?.product_name} ({issue.existing.scope_text}; confirmed by {issue.existing.confirmed_by || 'unknown'})</div>
      )}
      <div className="flex gap-1">
        {issue.code === 'memory_conflict' && onCorrect && (
          <Button size="sm" variant="outline" className="h-5 text-[10px]" disabled={busy} onClick={onCorrect} data-testid={`${testIdPrefix}-correct`}>Correct remembered identity</Button>
        )}
        <Button size="sm" variant="ghost" className="h-5 text-[10px]" onClick={onDismiss} data-testid={`${testIdPrefix}-issue-dismiss`}>Dismiss</Button>
      </div>
    </div>
  );
}

export const identityErrorDetail = (err) => {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 409 && d && typeof d === 'object') return d;
  return null;
};

const KIND_TEXT = { identity: 'Same product · remembered', line_only: 'This line only · not remembered', remembered: 'From remembered identity' };
export function ReviewKindTag({ kind, testId }) {
  if (!kind) return null;
  return <span className={`text-[10px] ${kind === 'line_only' ? 'text-slate-500' : 'text-sky-700'}`} data-testid={testId}>{KIND_TEXT[kind] || kind}</span>;
}

/** Two explicit choices per candidate: identity (eligibility-checked) vs line/row only. */
export function ChoiceButtons({ onIdentity, onLineOnly, busy, lineOnlyLabel = 'This line only', testIdPrefix }) {
  return (
    <div className="flex flex-col gap-1 shrink-0">
      <Button size="sm" className="h-6 text-[10px]" disabled={busy} onClick={onIdentity} title="Confirm this is the same product and remember it for this source identity" data-testid={`${testIdPrefix}-identity`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Same product · remember'}
      </Button>
      <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={busy} onClick={onLineOnly} title="Use for this line only (substitution or one-off); nothing is remembered" data-testid={`${testIdPrefix}-line-only`}>
        {lineOnlyLabel}
      </Button>
    </div>
  );
}
